import React from 'react';
import { WandSparkles, Bot, GraduationCap, Check } from 'lucide-react';
import MarketingPage, { ContentCard } from '@/components/marketing/MarketingPage';
import { pageJsonLd, faqJsonLd } from '@/components/shared/Seo';

const FAQ = [
  { q: 'Is Numuv free for job seekers?',
    a: 'Yes. Applicants create a profile, receive AI matches and track their hiring journey for free. Optional AI extras such as extra interview-prep questions are paid with Mu Points credits, and you always confirm before any credits are spent.' },
  { q: 'How does Numuv match me to jobs?',
    a: 'Your profile and resume are embedded and compared to live roles using vector similarity, then an LLM explains the fit with a match percentage and a skill-gap breakdown. Matching considers expected compensation and notice period, not just keywords.' },
  { q: 'Who is Jay?',
    a: 'Jay is Numuv\u2019s AI interviewer and career coach. Jay runs structured voice or video screening interviews on your schedule, transcribes every answer and scores competencies. Recruiters always make the final decision.' },
  { q: 'Can I use Numuv from WhatsApp?',
    a: 'Yes. You can sign up, upload a resume, ask for new matches and run interview prep entirely inside WhatsApp on Numuv\u2019s business number.' },
];

const JSONLD = pageJsonLd({
  name: 'Numuv for applicants',
  description: 'Build one profile, get AI-matched to roles that fit, practise interviews with Jay, and track every application to a signed offer.',
  path: '/for-applicants',
  breadcrumbs: [{ name: 'For applicants', path: '/for-applicants' }],
  extra: [faqJsonLd(FAQ)],
});

export default function ForApplicants() {
  return (
    <MarketingPage
      jsonLd={JSONLD}
      faq={FAQ}
      seoTitle="For Applicants — build a profile, get matched, ace interviews"
      seoDescription="Applicants on Numuv build a rich profile once, get AI-matched to roles that truly fit, practise with Jay (AI mock interviews), and track selection to signed offer."
      seoPath="/for-applicants"
      seoKeywords="job seeker AI, resume matching, AI interview practice, mock interview AI, candidate platform"
      eyebrow="For Applicants"
      title="One profile. Every relevant role. Zero busywork."
      subtitle="Stop tailoring resumes to keyword-hungry ATSs. Build your Numuv profile once and let AI surface the roles where you'll actually thrive."
    >
      <div className="grid md:grid-cols-3 gap-5 md:gap-7 mt-4">
        {[
          { icon: WandSparkles, color: '#12B6A6', title: 'Auto-matching', desc: 'Vector-powered engine scores roles against your skills, experience, and preferences — not just keywords.' },
          { icon: Bot, color: '#6D5EF7', title: 'Jay interviews on demand', desc: 'Complete AI screening interviews on your schedule. No calendar tetris, no ghosting.' },
          { icon: GraduationCap, color: '#8B5CF6', title: 'Mock prep simulator', desc: 'Rehearse role-specific questions with instant feedback on communication and content.' },
        ].map((f) => (
          <ContentCard key={f.title}>
            <span className="grid h-12 w-12 place-items-center rounded-[14px]" style={{ background: `${f.color}1F`, color: f.color }}><f.icon className="w-5 h-5" /></span>
            <h3 className="dis mt-5 text-[19px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>{f.title}</h3>
            <p className="mt-2 text-[14px] leading-[22px] t3">{f.desc}</p>
          </ContentCard>
        ))}
      </div>

      <ContentCard className="mt-10">
        <h3 className="dis text-[20px] md:text-[22px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>What you get</h3>
        <ul className="mt-5 grid md:grid-cols-2 gap-3 md:gap-4 text-[14px] t2">
          {[
            'Structured profile with resume auto-parsing',
            'Real-time match percentages for every role',
            'Video + audio AI interviews with Jay',
            'Objective competency scoring and feedback',
            'Prep simulator with role-specific drills',
            'Track selection → offer letter in one place',
            'Public share link for your best scorecard',
            'Free to start — no credit card required',
          ].map((b) => (
            <li key={b} className="flex items-center gap-2.5"><Check className="w-4 h-4 text-[#12B6A6] shrink-0" />{b}</li>
          ))}
        </ul>
      </ContentCard>
    </MarketingPage>
  );
}
